<script setup lang="ts">
import { defineProps, onMounted, ref, watch } from 'vue'
import { useToast } from 'vue-toast-notification'
import { useAvatarCacheStore } from '@/stores/useAvatarCacheStore'
import { getEmailInitials } from '@/utils/textUtils'

const $toast = useToast()
const avatarCache = useAvatarCacheStore()

const props = defineProps({
  email: {
    type: String,
    required: false,
  },
  size: {
    type: String,
    default: '48',
  },
})

const photoUrl = ref<string | null>(null)
const clickCount = ref(0)
const isExploding = ref(false)

const handleClick = () => {
  clickCount.value++
  if (clickCount.value == 3) {
    isExploding.value = true
    $toast.warning('Un easter egg trouvé 😁')
    setTimeout(() => {
      isExploding.value = false
      clickCount.value = 0
    }, 2000)
  }
}

const fetchUserPhoto = async (email: string | undefined) => {
  if (!email) {
    photoUrl.value = null
    return
  }
  photoUrl.value = await avatarCache.getPhotoUrl(email, props.size)
}

watch(
  () => props.email,
  (newEmail) => {
    fetchUserPhoto(newEmail)
  },
  { immediate: false },
)

onMounted(() => {
  fetchUserPhoto(props.email)
})
</script>

<template>
  <div v-if="isExploding" class="fire-explosion">
    <div class="flame flame-1"></div>
    <div class="flame flame-2"></div>
    <div class="flame flame-3"></div>
    <div class="flame flame-4"></div>
    <div class="flame flame-5"></div>
    <div class="flame flame-6"></div>

    <div class="explosion-core"></div>

    <div
      v-for="i in 15"
      :key="i"
      class="spark"
      :style="`--spark-angle: ${i * 24}deg; --delay: ${i * 0.05}s`"
    ></div>

    <div class="shockwave"></div>
  </div>

  <img
    v-if="photoUrl"
    :src="photoUrl"
    alt="Avatar"
    @click="handleClick"
    :class="['rounded-full transition-all duration-300 relative z-10', { exploding: isExploding }]"
  />
  <div
    v-else
    :class="'bg-gray-300 rounded-full flex items-center justify-center relative z-10 avatar-initials'"
    :style="{ width: `${size}px`, height: `${size}px`, maxWidth: '100%', maxHeight: '100%' }"
  >
    <span class="text-white font-bold initials-text">
      {{ getEmailInitials(email!) }}
    </span>
  </div>
</template>

<style scoped>
.fire-explosion {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  pointer-events: none;
  z-index: 20;
}

.flame {
  position: absolute;
  background: radial-gradient(
    ellipse at bottom,
    #ff4500 0%,
    #ff6347 20%,
    #ffa500 40%,
    #ffff00 60%,
    #ff0000 80%,
    transparent 100%
  );
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  animation: flame-dance 2s ease-out forwards;
}

.flame-1 {
  width: 60px;
  height: 120px;
  top: -60px;
  left: 70px;
  animation-delay: 0s;
  transform-origin: bottom center;
}

.flame-2 {
  width: 45px;
  height: 90px;
  top: -30px;
  left: 30px;
  animation-delay: 0.1s;
  transform-origin: bottom center;
}

.flame-3 {
  width: 50px;
  height: 100px;
  top: -40px;
  left: 120px;
  animation-delay: 0.15s;
  transform-origin: bottom center;
}

.flame-4 {
  width: 40px;
  height: 80px;
  top: 20px;
  left: 10px;
  animation-delay: 0.2s;
  transform-origin: bottom center;
}

.flame-5 {
  width: 55px;
  height: 110px;
  top: 10px;
  left: 140px;
  animation-delay: 0.25s;
  transform-origin: bottom center;
}

.flame-6 {
  width: 35px;
  height: 70px;
  top: 60px;
  left: 80px;
  animation-delay: 0.3s;
  transform-origin: bottom center;
}

.explosion-core {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  background: radial-gradient(
    circle,
    #ffffff 0%,
    #ffff00 20%,
    #ff4500 40%,
    #ff0000 70%,
    #8b0000 100%
  );
  border-radius: 50%;
  animation: explosion-blast 2s ease-out forwards;
}

.spark {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #ffff00, #ff4500);
  border-radius: 50%;
  animation: spark-fly 2s ease-out forwards;
  animation-delay: var(--delay);
}

.shockwave {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: 100px;
  border: 3px solid rgba(255, 69, 0, 0.8);
  border-radius: 50%;
  animation: shockwave-expand 2s ease-out forwards;
}

.exploding {
  animation: avatar-explode 2s ease-out forwards;
}

@keyframes flame-dance {
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }
  20% {
    transform: scale(1.2) rotate(-10deg);
    opacity: 1;
  }
  40% {
    transform: scale(1.5) rotate(15deg);
    opacity: 0.9;
  }
  60% {
    transform: scale(1.8) rotate(-20deg);
    opacity: 0.7;
  }
  80% {
    transform: scale(2.2) rotate(25deg);
    opacity: 0.4;
  }
  100% {
    transform: scale(3) rotate(-30deg);
    opacity: 0;
  }
}

@keyframes explosion-blast {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 1;
    filter: brightness(3);
  }
  30% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 1;
    filter: brightness(5);
  }
  60% {
    transform: translate(-50%, -50%) scale(2.5);
    opacity: 0.8;
    filter: brightness(3);
  }
  100% {
    transform: translate(-50%, -50%) scale(4);
    opacity: 0;
    filter: brightness(1);
  }
}

@keyframes spark-fly {
  0% {
    transform: translate(-50%, -50%) rotate(var(--spark-angle)) translateX(0) scale(1);
    opacity: 1;
  }
  50% {
    transform: translate(-50%, -50%) rotate(var(--spark-angle)) translateX(100px) scale(1.5);
    opacity: 0.8;
  }
  100% {
    transform: translate(-50%, -50%) rotate(var(--spark-angle)) translateX(200px) scale(0);
    opacity: 0;
  }
}

@keyframes shockwave-expand {
  0% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 1;
  }
  50% {
    transform: translate(-50%, -50%) scale(2);
    opacity: 0.6;
  }
  100% {
    transform: translate(-50%, -50%) scale(5);
    opacity: 0;
  }
}

@keyframes avatar-explode {
  0% {
    transform: scale(1);
    opacity: 1;
    filter: brightness(1);
  }
  20% {
    transform: scale(1.1);
    opacity: 1;
    filter: brightness(2);
  }
  50% {
    transform: scale(0.8);
    opacity: 0.8;
    filter: brightness(5) saturate(2);
  }
  80% {
    transform: scale(0.3);
    opacity: 0.3;
    filter: brightness(10) saturate(5);
  }
  100% {
    transform: scale(0);
    opacity: 0;
    filter: brightness(20) saturate(10);
  }
}

@keyframes pre-explosion {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 rgba(255, 69, 0, 0);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(255, 69, 0, 0.5);
  }
}

.avatar-initials {
  aspect-ratio: 1/1;
  overflow: hidden;
  container-type: inline-size;
}

.initials-text {
  font-size: 38cqi;
  line-height: 1;
  text-align: center;
}
</style>
